/**
 * Authentication
 */
import jsCookie from 'js-cookie';
import Router from 'next/router';
import selectn from 'selectn';
import { setLoginModalStoreData } from 'store/actions/login.actions';

import { Country, Language } from '../config';
import { INDEX, WebRoute } from '../config/fe-routes';
import { setLanguage } from '../hooks/useLanguage';
import AuthService from '../services/auth.service';
import LoginCheckoutService from '../services/login-checkout.service';
import store from '../store';
import { removeUser, setLoginLoading, setUser } from '../store/actions/user.actions';
import { UserType } from '../types/user.types';
import { GTM } from './analytics';
import { Braze } from './braze';
import i18n, { getCountry, getLanguage } from './i18n';
import { cleanQueryParams, getQueryParams, redirectTo, stringifyQueryParams } from './route-helpers';
import { notification } from './snackbar';

const authService = new AuthService();
const LOGIN_TARGET_STORAGE_KEY = 'login-target';

/**
 * Refreshes token and checks if user is logged in (CLIENT SIDE ONLY!)
 * > User visits webpage:
 *      IF Token does not exist
 *          - Do nothing
 *
 *      IF Token exists
 *          - Refresh token
 *
 *          IF Refresh token succeeded
 *              - Save token
 *              - Save user data if exists
 *              - Check user.role === "user" to see if logged in
 *          IF Refresh token failed
 *              - Do nothing
 *
 *      IF nonce code exists in url
 *          - Get new token
 *          - Save token
 *          - Save user
 *          - Check user.role === "user" to see if logged in
 *          - Remove "nonce" from url
 *          - Redirect to url user wanted to go
 */
export const checkLogin = async () => {
  // Run client side only
  if (typeof window !== 'undefined') {
    // console.log('inside client side')
    // Get current token
    const currentToken = jsCookie.get('token');
    // Get nonce
    const nonce = getQueryStringValue('nonce');
    // Get isNewAccount
    const isNewAccount = getQueryStringValue('isNewAccount') === 'true';
    // Get login redirect url
    const redirectAfterLogin = getLoginTarget();

    // If current token exists
    if (currentToken) {
      setLoginLoading(true);

      // Refresh token by making a request with token that was saved in previous step (attached automatically to request)
      let tokenResponse;

      try {
        tokenResponse = await authService.refreshToken();
      } catch (e) {
        tokenResponse = {};
      }

      const { token: newToken, user: newUser } = tokenResponse;

      // Get user language and currently selected language so we can compare them.
      const userLanguage = newUser?.language as Language;
      const currentLanguage = getLanguage();

      const userCountry = newUser?.country as Country;
      const currentCountry = getCountry();

      // If language was changed in DB, we will apply the update here.
      if (userLanguage && currentLanguage !== userLanguage) {
        await setLanguage({
          language: userLanguage,
          country: userCountry || currentCountry,
          saveToDb: false,
        });
      }

      setLoginLoading(false);
      saveUser({ ...newUser }, newToken);
    }

    // If nonce exists in url, use it to get user token
    const user = getUserFromStore();

    if (nonce) {
      setLoginLoading(true);

      // Remove nonce from url
      const queryParams = getQueryParams();

      delete queryParams.nonce;
      try {
        window.history.replaceState(
          '',
          '',
          window.location.origin + window.location.pathname + stringifyQueryParams(cleanQueryParams(queryParams)),
        );
      } catch (e) {
        console.error(e);
      }

      // Get token using nonce only if user is not logged in yet
      if (!user?.isLoggedIn) {
        // Get token and user data
        try {
          const { token: newToken, user: newUser } = (await authService.loginCallback(nonce, isNewAccount)) || {};

          const userLanguage = newUser?.language as Language;
          const currentLanguage = getLanguage();

          const userCountry = newUser?.country as Country;
          const currentCountry = getCountry();

          // Set user language only for logged-in users, not for guest users
          // This prevents guest users from overriding the user's locale preference
          if (userLanguage && currentLanguage !== userLanguage) {
            await setLanguage({
              language: userLanguage,
              country: userCountry || currentCountry,
              saveToDb: false,
            });
          }

          // Update analytics user properties with logged-in user's language
          GTM.updateUserProperties({
            user_language: userLanguage || currentLanguage,
            user_country: userCountry || currentCountry,
          });

          // Throw error if didn't get new token
          if (!newToken) {
            setLoginLoading(false);

            return notification.showErrorNotification(
              { message: i18n.t('common.failed-to-login'), error: null },
              { preventDuplicate: true },
            );
          }

          setLoginLoading(false);
          saveUser({ ...newUser }, newToken);

          // Redirect to where user wanted to go
          await redirectTo(redirectAfterLogin, true);
          // Remove login target
          removeLoginTarget();

          return;
        } catch (error) {
          setLoginLoading(false);

          const message = error?.data?.data?.type === 'banned' ? error?.data?.data?.msg : i18n.t('common.failed-to-login');

          return notification.showErrorNotification(
            { message, error },
            {
              preventDuplicate: true,
            },
          );
        }
      } else {
        // If user not logged in
        // Redirect to where user wanted to go
        await redirectTo(redirectAfterLogin, true);
        // Remove login target
        removeLoginTarget();
      }
    }

    setLoginLoading(false);
  }
};

function saveUser(user, token) {
  // Update token in cookies (must be first thing to do here, so next requests get authorized)
  if (!token) {
    jsCookie.remove('token');
  }

  // Update user in store
  setUser({
    ...(user || {}), // User may be undefined
    isLoggedIn: user?.role === 'user',
  });

  // Identify user in Braze if logged in (respects marketing consent)
  if (user?.role === 'user') {
    Braze.changeUser(user);
  }
}

/**
 * Gets value from query by passed key
 * Works for legacy browser as well
 */
function getQueryStringValue(key): string {
  // eslint-disable-next-line no-useless-escape,max-len
  return decodeURIComponent(
    window.location.search.replace(
      new RegExp(
        '^(?:.*[&\\?]' +
          // eslint-disable-next-line no-useless-escape
          encodeURIComponent(key).replace(/[\.\+\*]/g, '\\$&') +
          '(?:\\=([^&]*))?)?.*$',
        'i',
      ),
      '$1',
    ),
  );
}

/**
 * Sets login target to localstorage
 */
function setLoginTarget(loginTarget: WebRoute) {
  if (loginTarget) {
    window.localStorage.setItem(LOGIN_TARGET_STORAGE_KEY, JSON.stringify(loginTarget));
  }
}

/**
 * Gets login target from localstorage
 */
function getLoginTarget(): WebRoute {
  // Get redirect target
  const loginRedirectStr = window?.localStorage?.getItem(LOGIN_TARGET_STORAGE_KEY);

  try {
    return loginRedirectStr ? JSON.parse(loginRedirectStr) : INDEX();
  } catch (err) {
    return INDEX();
  }
}

/**
 * Check if login target is in localstorage
 */
export function loginTargetExists() {
  return !!window?.localStorage?.getItem(LOGIN_TARGET_STORAGE_KEY);
}

/**
 * Remove login target from localstorage
 */
function removeLoginTarget() {
  // Remove redirect target from local storage
  window?.localStorage?.removeItem(LOGIN_TARGET_STORAGE_KEY);
}

function getCurrentRoute(): WebRoute {
  const { asPath: as, route: href } = Router;

  return {
    href,
    as,
  };
}

/**
 * Adds guest token to the user
 * > User visits "add product" flow
 *      IF Token does not exist
 *          - Get guest token
 *          - Save guest token
 *      IF Token exists
 *          - Do nothing
 */
export const initGuest = async () => {
  // Check current login token
  await checkLogin();

  // Run client side only
  if (typeof window !== 'undefined') {
    const currentToken = jsCookie.get('token');

    // If there is no token
    if (!currentToken) {
      try {
        // Get guest token
        const { token: newGuestToken, user: newGuestUser } = await authService.getGuestToken();

        // Update token in store (so new requests will use new token) and in cookies
        if (newGuestToken) {
          saveUser(newGuestUser, newGuestToken);
        }
      } catch (error) {
        notification.showErrorNotification(
          { error },
          {
            preventDuplicate: true,
          },
        );
      }
    }
  }
};

/**
 * Logs user out
 */
export const logOut = (skipRedirectToHome?: boolean) => {
  // Just remove token and reload main page
  jsCookie.remove('token');

  // Redirect to home
  if (!skipRedirectToHome) {
    window.location.href = INDEX().as;
  }

  const loginCheckoutService = new LoginCheckoutService();

  loginCheckoutService.clearCheckoutData();
  setLoginModalStoreData(null);

  // Remove use from store
  removeUser();
};

/**
 * Logs user in
 */
export const logInWithFacebook = async (redirect: WebRoute = getCurrentRoute(), opts: { publishProductOnLogin?: boolean } = {}) => {
  const user: UserType = getUserFromStore();

  if (!user?.isLoggedIn) {
    setLoginLoading(true);

    try {
      setLoginTarget(redirect);

      return authService.loginWithFacebook(opts);
    } catch (error) {
      setLoginLoading(false);
      notification.showErrorNotification(
        {
          message: i18n.t('common.failed-to-login'),
          error,
        },
        {
          preventDuplicate: true,
        },
      );
    }
  }

  setLoginLoading(false);

  return notification.showSuccessNotification(i18n.t('common.user-already-logged-in'), { preventDuplicate: true });
};
export const logInWithGoogle = async (redirect: WebRoute = getCurrentRoute(), opts: { publishProductOnLogin?: boolean } = {}) => {
  const user: UserType = getUserFromStore();

  if (!user?.isLoggedIn) {
    setLoginLoading(true);

    try {
      setLoginTarget(redirect);

      return authService.loginWithGoogle(opts);
    } catch (error) {
      setLoginLoading(false);
      const message = error?.data?.data?.type === 'banned' ? error?.data?.data?.msg : i18n.t('common.failed-to-login');

      notification.showErrorNotification({ message, error }, { preventDuplicate: true });
    }
  }

  setLoginLoading(false);

  return notification.showSuccessNotification(i18n.t('common.user-already-logged-in'), { preventDuplicate: true });
};

/**
 * Gets user from store
 */
export function getUserFromStore(): UserType {
  return selectn('userData.user', store.getState()) || {};
}
